import {
	IInheritedShareableStore, 
	RightsRestrictions, 
	CreatedLinkShare,
	LinkSecurity,
	LinkCreationError,
	UserGroupLinkRestriction
} from 'sharebase/domain';
import { 
	AuthService, 
	AclService, 
	ProgressService 
} from 'phoenix/services';
import { 
	Acl,
	LinkExpirationMeaning,
	ShareBaseDocument, 
	ShareBaseFolder, 
	ShareBaseLibrary,
	pxItemTypeGuards,
	RightsOption,
	RightsType,
	PermissionType,
	TypeAheadUsers,
	TypeAheadUserGroups,
	UserGroupLight,
	PhoenixUser,
	LinkCreationException
} from 'phoenix/domain';
import { ShareAction } from './shareDialog.actions';
import UsersService from 'sharebase/services/usersService';
import {
	IUserGroupsService,
	ToastService,
	TrackingService
} from 'sharebase/services';

export enum UserShareTab {
	View = 0,
	Create = 1,
	Edit = 1
}
export enum LinkShareTab {
	View = 0,
	Create = 3,
	Edit = 3
} 

export type ShareTab = UserShareTab | LinkShareTab;
export interface ShareDialogTabs {
	UserShare: typeof UserShareTab,
	LinkShare: typeof LinkShareTab
}

export const ShareDialogTabs:ShareDialogTabs = Object.freeze({
	UserShare: UserShareTab,
	LinkShare: LinkShareTab
});

interface LinkCreationModel {
	availableOptions:RightsRestrictions;
	vmData: {
		right: RightsOption;
		securityType: LinkSecurity;
		maxExpirationDateTime: Date | string;
		requiresAdvancedOps: boolean;
		subject: string;
		verifyPassword: string;
	},
	postData: {
		CustomEmailText: string;
		Embedded: {
			Permissions: any[];
		},
		ExpireMeaning: LinkExpirationMeaning;
		ExpirationValue: number;		
		ExpiresOn: Date;
		Password: string;
		PinRequired: boolean;
		Recipients: string[];
	}
}
type Shareable = PhoenixUser|UserGroupLight;	

require('./shareDialog.styles');
export function ShareDialogController(
	$state,
	$window:ng.IWindowService, 
	$q:ng.IQService, 
	$mdDialog, 
	$mdMedia, 
	$mdConstant, 
	pxAclService:AclService, 
	authService:AuthService, 
	progressService:ProgressService, 
	usersService:UsersService,
	userGroupsService:IUserGroupsService, 
	sbToastService:ToastService, 
	clipboardService, 
	context:ShareBaseDocument | ShareBaseFolder | ShareBaseLibrary, 
	targetStep:ShareTab, 
	targetShareList:number, 
	aclToEdit:Acl, 
	datastore:IInheritedShareableStore,
	gettextCatalog,
	TrackingService:TrackingService
) {
	const newLinkTemplate:LinkCreationModel = {
		availableOptions: null,
		vmData:{
			right: null,
			verifyPassword:null,
			securityType: LinkSecurity.None,
			maxExpirationDateTime: null,
			requiresAdvancedOps:false,
			subject:gettextCatalog.getString('Subject: A document has been shared with you')
		},
		postData: {
			CustomEmailText: '',
			Embedded: {Permissions:[]},
			ExpirationValue: 0,
			ExpireMeaning: LinkExpirationMeaning.Never,
			ExpiresOn:null,
			Password: '',
			PinRequired: false,
			Recipients: []
		}
	};

	targetStep = targetStep ? targetStep : 0;
	var vm = this;
	var today = new Date();
	var initTasks = [
		datastore.getCurrentUserAcl(),
		datastore.getPermissions(aclToEdit),
		usersService.getUsersTypeAhead(),
		userGroupsService.getUserGroupsTypeAhead(),
		datastore.getShares().then(() => datastore.getLinks())
	];
	var advancedOptions = {};
	var shareables:Shareable[] = [];
	var contentName = pxItemTypeGuards.isShareBaseDocument(context) ? context.DocumentName : (context as ShareBaseFolder).FolderName;
	var linkRights:RightsRestrictions;
	var currentUserAcl:Acl;

	vm.isLoading = false;
	vm.initialTargetStep = targetStep;
	vm.minLinkAuthType = UserGroupLinkRestriction.None;
	vm.DialogTabs = ShareDialogTabs;
	vm.LinkSecurity = LinkSecurity;
	vm.UserGroupLinkRestriction = UserGroupLinkRestriction;
	vm.newLink;
	vm.getTitle = getTitle;
	vm.tabIndex = 0;
	vm.shareListIndex = targetShareList ? targetShareList : 0;		
	vm.deploymentId = authService.deploymentId;
	vm.shareLink = null;
	vm.datastore = datastore;
	vm.mdMedia = $mdMedia;
	vm.changeView = changeView;
	vm.createNew = createNew;
	vm.sendShareEmail = sendShareEmail;
	vm.emailChipSeperators = [
		$mdConstant.KEY_CODE.COMMA, 
		$mdConstant.KEY_CODE.SEMICOLON, 
		$mdConstant.KEY_CODE.ENTER, 
		$mdConstant.KEY_CODE.SPACE
	];
	vm.availableRights = [];
	vm.availableAdvancedOptions = [];
	vm.context = normalizeContext(context);		
	vm.emailSubject = gettextCatalog.getString('You\'ve been shared {{contentName}}', {contentName: encodeURIComponent(contentName)});
	vm.minExpireDate = new Date();
	vm.share = {
		users: [],
		right: null
	};
	vm.readOnly = true;
	vm.allShares = datastore.shares.concat(datastore.inheritedShares);

	vm.disableCreateUpdate = false;
	vm.currentLocale = gettextCatalog.getCurrentLanguage();
	vm.TrackingService = TrackingService;

	vm.getLinkSecuritySelectedText = function(selection:LinkSecurity){
		if (selection == LinkSecurity.None){
			return gettextCatalog.getString('None');
		} else if (selection === LinkSecurity.AccessCode){
			return gettextCatalog.getString('Email Access Code');
		} else if (selection === LinkSecurity.Password){
			return gettextCatalog.getString('Custom Password');
		} else {
			return '';
		}
	}
	vm.shareCreatedMessage = gettextCatalog.getString('Share created successfully');
	vm.shareUpdatedMessage = gettextCatalog.getString('Share updated successfully');
	vm.createNewShare = () => createNew(UserShareTab.Create);
	vm.createNewLink = () => createNew(LinkShareTab.Create);

	Object.defineProperties(vm, {
		'inEditMode' : {
			get: () => !!aclToEdit
		}
	});
	
	vm.linkStartDate = today;
	
	vm.revokeLink = function(revokedLink) {		
		vm.TrackingService.trackClick('Revoke share', 'Link share');
		return vm.datastore.revokeLink(revokedLink);
	}

	vm.deleteShare = function(deletedShare) {	
		vm.TrackingService.trackClick('Revoke share', 'User share');
		return vm.datastore.deleteShare(deletedShare);
	}

	vm.openURL = function (url) {
		$window.open(url);
	};
	vm.close = function () {
		$mdDialog.hide();
	};

	vm.viewLinks = () => {
		changeView(LinkShareTab.View, 1);
	};

	vm.viewShares = () => {
		changeView(UserShareTab.View, 0);
	}

	vm.createUpdateShare = guardFormAction(() => {
		 
		vm.disableCreateUpdate = true;
		let p:ng.IPromise<Acl|Acl[]>;

		if(vm.inEditMode){
			vm.TrackingService.trackClick('Updated rights: ' + JSON.stringify(vm.share.right), 'User share'); 
			vm.TrackingService.trackClick('Updated user count: ' + vm.share.users.length, 'User share'); 
			vm.TrackingService.trackClick('Updated options: ' + JSON.stringify(vm.availableAdvancedOptions), 'User share');
			vm.TrackingService.trackClick('Share updated', 'User share'); 
			p = datastore.updateShare(aclToEdit, vm.share.right, vm.availableAdvancedOptions)
		} else {			
			vm.TrackingService.trackClick('Created rights: ' + JSON.stringify(vm.share.right), 'User share'); 
			vm.TrackingService.trackClick('Created user count: ' + vm.share.users.length, 'User share'); 
			vm.TrackingService.trackClick('Created options: ' + JSON.stringify(vm.availableAdvancedOptions), 'User share');
			vm.TrackingService.trackClick('Share created', 'User share');
			p = datastore.createShares(vm.share.users, vm.share.right, vm.availableAdvancedOptions)
		}			

		p.then(response => {
			vm.shareLink = Array.isArray(response) ? response[0].webPath : response.webPath;
			changeView(2);
			// update share datastore to get new ids
			return datastore.getShares();
		})
		.finally(() => {
			vm.disableCreateUpdate = false;
		});

		return p;
	});

	vm.validateEmail = function($chip){
		var emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		if(!emailRegex.test($chip)){
			sbToastService.showSimple(gettextCatalog.getString($chip + ' is an invalid email address.'));
			return null;
		}

		return $chip;

	};

	// https://regex101.com/r/FLihb8/1
	vm.verifyPasswordPattern = function(password){
		if (password) {
			// js replacement strings include certain replacement patterns.
			// '$&' returns the matched substring and '\\' prepends it with an escape slash.
			return password.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
		}
	}

	vm.updateAdvancedOptions = function () {
		vm.availableAdvancedOptions = advancedOptions[vm.share.right];
		vm.availableAdvancedOptions.forEach(vm.checkAllowFlags);
	};

	vm.createLink = function () {
		vm.disableCreateUpdate = true;								
		vm.error = null;
		if (vm.linkFormOps.$invalid || (vm.newLink.vmData.securityType === LinkSecurity.AccessCode && vm.newLink.postData.Recipients.length === 0)) {
			changeView(4);
			return;
		}

		vm.newLink.postData.PinRequired = vm.newLink.vmData.securityType === LinkSecurity.AccessCode;

		
		vm.TrackingService.trackClick('Created rights: ' + JSON.stringify(vm.newLink.vmData.right), 'Link share'); 
		vm.TrackingService.trackClick('Created user count: ' + vm.newLink.postData.Recipients.length, 'Link share'); 
		vm.TrackingService.trackClick('Created security: ' + vm.newLink.vmData.securityType, 'Link share');
		vm.TrackingService.trackClick('Created experation: ' + vm.newLink.postData.ExpireMeaning, 'Link share');
		vm.TrackingService.trackClick('Created with message: ' + vm.newLink.postData.CustomEmailText !== '', 'Link share');		
		vm.TrackingService.trackClick('Share created', 'Link share');

		setLoading(true);
		return datastore.createLink(vm.newLink.postData, vm.newLink.vmData.right)
			.then(function (newLink) {
				// manually add this data since the API doesn't provide it on creation
				newLink.Settings = vm.newLink.vmData.securityType;
				if(vm.newLink.vmData.securityType !== LinkSecurity.AccessCode) {
					vm.shareLink = newLink.LinkUrl ; 
					changeView(5);
				} else {
					changeView(0, 1);
				}
			})
			.catch((e:LinkCreationException) => {
				vm.error = e.userMessage;
			})
			.finally(function(){					
				setLoading(false);
				vm.disableCreateUpdate = false;
			});
	};

	vm.editShare = function(share:Acl, revoke:boolean = false){
		var editView = share.isLinkAcl ? 3 : 1;
		
		vm.TrackingService.trackClick('Edit share', share.isLinkAcl ? 'Link share' : 'User share' );

		vm.initialTargetStep = 0;
		aclToEdit = share;

		if(share.isInherited && !revoke){
			$state.go('content.myFiles.folder', {					
				folderID: share.sourceId
			});
		}
		else {
			datastore.getPermissions(aclToEdit)
				.then(rr => {	
					if(revoke) {
						rr.rights = rr.rights.filter(right => right.id === RightsType.NOACCESS);
					}
					populate(rr, share);				
					changeView(UserShareTab.Edit);
				});
		}
	};

	vm.revokeAccess = function(share:Acl){
		vm.editShare(share, true);
	};

	function sendShareEmail(){
		if(!vm.sharedUserEmail.subject || !vm.sharedUserEmail.message){ return; }
		vm.TrackingService.trackClick('Email all shared users', 'User share');
		progressService.visible = true;
		datastore.messageSharedUsers(vm.sharedUserEmail.subject, vm.sharedUserEmail.message)
			.then(function(response){					
				sbToastService.showSimple(gettextCatalog.getString('Message sent to all shared users.'));
				changeView(0);
			})
			.catch(function(e){								
				sbToastService.showSimple(gettextCatalog.getString('Unable to send message.'));
			})
			.finally(function(){
				progressService.visible = false;
			});
	}
	function getTitle () {
		let title;
		switch (vm.tabIndex) {
			case 1:
				title = aclToEdit ? gettextCatalog.getString('Edit Share') : gettextCatalog.getString('Create User Share');
				break;
			case 2:
				title = gettextCatalog.getString('User Share');
				break;
			case 3:
			case 4:
				title = aclToEdit ? gettextCatalog.getString('Edit Link') : gettextCatalog.getString('Create A Link');
				break;
			case 5:
				title = aclToEdit ? gettextCatalog.getString('Link Updated') : gettextCatalog.getString('New Link Created');
				break;
			case 6:
				title = gettextCatalog.getString('Email Shared Users');
				break;
			case 0:
			default:
				title =  gettextCatalog.getString('Share Details');
				break;
		}

		return title;
	};

	vm.copyURL = function () {
		try {
			clipboardService.copy(vm.shareLink);
			sbToastService.showSimple(gettextCatalog.getString('Share link copied to clipboard.'));
		} catch (e) {
			sbToastService.showSimple(gettextCatalog.getString('Could not copy link.'));
		}
	};
	// validates the advanced option to determine if it should affect 
	// others when enabled/disabled and does so when appropriate
	vm.checkAllowFlags = function (ao) {
		var constraint = ao.constraints;
			if (constraint.length > 0) {
				var i = 0;
				// iterate on constraint field characters, 
				// which represent a command and id of the right it modifies	
				while (i < constraint.length) {
					var cmd = constraint.charAt(i++);
					var idx = +(constraint.charAt(i++));
					var refAo = vm.availableAdvancedOptions[idx];
					if (cmd === '+') {
						if (ao.value) {
							refAo.disabled = true;
							refAo.value = true;
					} else {
						refAo.disabled = false;
					}
				}
			}
		}
	};

	//set the default step to reduce the slide in motion on load
	vm.tabIndex = targetStep;  
	setLoading(true);
	$q.all(initTasks)
		.then(taskData => {
			currentUserAcl = taskData[0] as Acl;
			let users = taskData[2] as PhoenixUser[];
			let userGroups = taskData[3] as UserGroupLight[];
			vm.readOnly = !currentUserAcl.hasPermission(PermissionType.MODIFY);
			vm.canCreateShare = currentUserAcl.hasPermission(PermissionType.SHARE);
			vm.canCreateLink = currentUserAcl.canShareByLink;
			shareables = (users as Shareable[]).concat(userGroups);				
			populate(taskData[1] as RightsRestrictions, aclToEdit);								
		})
		.finally(() => {
			setLoading(false);	
			changeView(targetStep, vm.shareListIndex);
		});

	function populate(rr:RightsRestrictions, aclToEdit?: Acl) {			
		vm.availableRights = rr.rights;
		// massage advanced options into dict keyed on right ID
		vm.availableRights.forEach((right:RightsOption) => {
			advancedOptions[right.id]=  right.embedded.options;
		});
		vm.share.right = +vm.availableRights[vm.availableRights.length - 1].id;

		if(aclToEdit){								
			vm.share.users = [ findShareable(aclToEdit) ];
			if(aclToEdit.rightId in vm.availableRights){
				vm.share.right = aclToEdit.rightId;
			} 
		}

		vm.updateAdvancedOptions();
	}

	function guardFormAction(func) {
		let defaultError = gettextCatalog.getString('There was an issue sharing "{{name}}".', vm.context);
		let updateError = gettextCatalog.getString('There was an issue updating share "{{name}}".', vm.context);
		let invalidShare = gettextCatalog.getString('Please specify one or more valid users or groups.');

		if (!angular.isFunction(func)) { return angular.noop; }
		return function () {
				var res = $q.resolve();

				if (vm.f.$valid && vm.share.users.length > 0 && vm.share.right) {
					res = func.apply(null, arguments);
				} else {
					vm.error = vm.share.users.length > 0 ? defaultError : invalidShare;
				}

				res.catch(function (e) {
					vm.error = e.data.Message || (vm.inEditMode ? updateError : defaultError);
			});

			return res;
		};
	}

	function findShareable(aclToEdit:Acl) {
		var list = shareables.filter(shareable => aclToEdit.userId === shareable[aclToEdit.isGroup? 'UserGroupId': 'UserId']);
		return list.length > 0 ? list[0]: null;
	}
	
	function normalizeContext(context) {
		var normalizedContext = {
			id: '',
			type: '',
			name: ''
		};

		if (context.IsLibrary) {
			normalizedContext.id = context.LibraryId;
			normalizedContext.type = 'library';
			normalizedContext.name = context.LibraryName;
		} else if (context.FolderId) {
			normalizedContext.id = context.FolderId;
			normalizedContext.type = 'folder';
			normalizedContext.name = context.FolderName;
		} else if (context.DocumentId) {
			normalizedContext.id = context.DocumentId;
			normalizedContext.type = 'document';
			normalizedContext.name = context.DocumentName;
		}

		return normalizedContext;
	}

	function setLoading(isLoading:boolean = false) {
		vm.isLoading = progressService.visible = isLoading;
	}
	
	function createNew(viewIndex:ShareTab){
		aclToEdit = null;
		vm.share.users = [];
		changeView(viewIndex);
	}

	function changeView(viewIndex:ShareTab, shareListIndex:number = 0){
		var p: ng.IPromise<any> = $q.resolve();
		vm.shareListIndex = shareListIndex;
		switch(viewIndex){
			case 1:					
				vm.showAdvanced = false;
				break;
			case 2:
				break;
			case ShareDialogTabs.LinkShare.Create:				
				p = createNewLinkSettings().then(newLinkSettings => {						
					vm.newLink = newLinkSettings;	
				});
				break;
			case 6:			
				var limitToHide = $mdMedia('xs') ? 3 : 7
				vm.sharedUserEmail= {subject:null, message:null, showReceipents: vm.allShares.length < limitToHide};							
				break;
			default:
				break;				
		}

		p.finally(() => {
			vm.tabIndex = viewIndex;
		});			
	}

	function createNewLinkSettings() {
		// cache link rights after first retrieval
		let p = linkRights ? $q.resolve(linkRights) : datastore
			.getLinkRights(currentUserAcl)
			.then(rights => linkRights = rights);
			
		return p.then(function(restrictions){
			let newLink = angular.copy(newLinkTemplate);
			restrictions.rights = restrictions.rights.map(right => {
				switch(right.id) {
					case RightsType.EDIT:
						right.name = gettextCatalog.getString('Edit');
						break;
					case RightsType.VIEW:
						right.name = gettextCatalog.getString('View');
						break;
					case RightsType.UPLOADONLY:
						right.name = gettextCatalog.getString('Upload Only');
						break;
				}
				return right;
			});
			newLink.availableOptions = restrictions;
			newLink.vmData.right = (restrictions.rights || [])[0];
			vm.canCreateLink = restrictions.rights.length > 0;

			if(restrictions.minLinkAuthenticationType > 0){
				newLink.vmData.requiresAdvancedOps = true;
				vm.minLinkAuthType = restrictions.minLinkAuthenticationType;

				switch(restrictions.minLinkAuthenticationType) {
					case UserGroupLinkRestriction.RequirePin:
						newLink.vmData.securityType = LinkSecurity.AccessCode;
						newLink.postData.PinRequired = true;							
						break;
					case UserGroupLinkRestriction.Default:
						newLink.vmData.securityType = LinkSecurity.Default;
						break;
					case UserGroupLinkRestriction.None:
						newLink.vmData.securityType = LinkSecurity.None;
						break;
				}
			}

			switch(restrictions.maxLinkExpirationType){
				case 1:
					newLink.postData.ExpireMeaning = LinkExpirationMeaning.Days;
					break;
				case 2:
					newLink.postData.ExpireMeaning = LinkExpirationMeaning.Minutes;
					break;
				case 3:
					newLink.postData.ExpireMeaning = LinkExpirationMeaning.Hours;
					break;
				case 4:
					//specific day
					newLink.postData.ExpireMeaning = LinkExpirationMeaning.Date;
					break;
			}

			if(restrictions.maxLinkExpirationDays){
				//set max date
				newLink.postData.ExpirationValue = restrictions.maxLinkExpirationDays;
				let d = new Date;
				d.setDate(d.getDate() + restrictions.maxLinkExpirationDays);
				newLink.vmData.maxExpirationDateTime = d;
				newLink.postData.ExpiresOn = new Date(newLink.vmData.maxExpirationDateTime);
			} else {
				let exp = new Date(Date.now());
				exp.setDate(exp.getDate() + 1);
				newLink.postData.ExpiresOn = exp;
			}

			return newLink;
		});
	}
}

ShareDialogController.$inject = [
	'$state',
	'$window',
	'$q',
	'$mdDialog',
	'$mdMedia',
	'$mdConstant',
	'pxAclService',
	'pxAuthService',
	'pxProgressService',
	'sbUsersService',
	'sbUserGroupsService',
	'sbToastService',
	'pxClipboardService',
	'context',
	'targetStep',
	'targetShareList',
	'aclToEdit',
	'datastore',
	'gettextCatalog',
	'sbTrackingService'
];


// WEBPACK FOOTER //
// ./src/sharebase/components/shareDialog/shareDialog.ts